/**
 * Per-model pricing helpers for the Pricing tab: read/write one rate cell for a
 * currency, derive display rates, and detect edits against the server defaults.
 */

import type { Currency, ModelInfo, ModelPricing, TierBandRates } from '@tcc/shared';

export const TIERS = ['standard', 'priority', 'flexBatch'] as const;
export const BANDS = ['le200k', 'gt200k'] as const;
export const FIELDS = ['input', 'output', 'cached'] as const;
export type Tier = (typeof TIERS)[number];
export type Band = (typeof BANDS)[number];
export type Field = (typeof FIELDS)[number];

export const TIER_LABELS: Record<Tier, string> = {
	standard: 'Standard',
	priority: 'Priority',
	flexBatch: 'Flex / Batch',
};
export const BAND_LABELS: Record<Band, string> = { le200k: 'Up to 200K', gt200k: 'Over 200K' };
export const FIELD_LABELS: Record<Field, string> = { input: 'Input', output: 'Output', cached: 'Cached input' };

/**
 * Full tiered pricing for a model. Flat-rate models get every tier/band at the
 * flat rate with cached = input — the same rates the cost engine applies to them.
 */
export function usdPricing(m: ModelInfo): ModelPricing {
	if (m.pricing) return m.pricing;
	const flat = (): TierBandRates => ({
		input: m.inputPricePerMillion,
		output: m.outputPricePerMillion,
		cached: m.inputPricePerMillion,
	});
	return {
		standard: { le200k: flat(), gt200k: flat() },
		priority: { le200k: flat(), gt200k: flat() },
		flexBatch: { le200k: flat(), gt200k: flat() },
	};
}

/** Rates to show for a currency: native vendor pricing if present, else USD (× FX for non-USD). */
export function displayPricing(m: ModelInfo, ccy: Currency, fx: number): { pricing: ModelPricing; derived: boolean } {
	if (ccy === 'USD') return { pricing: usdPricing(m), derived: false };
	const native = m.pricingByCurrency?.[ccy];
	if (native) return { pricing: native, derived: false };
	const conv = (r: TierBandRates): TierBandRates => ({
		input: round(r.input * fx),
		output: round(r.output * fx),
		cached: round(r.cached * fx),
	});
	const p = usdPricing(m);
	const tier = (t: Tier) => ({ le200k: conv(p[t].le200k), gt200k: conv(p[t].gt200k) });
	return {
		pricing: { standard: tier('standard'), priority: tier('priority'), flexBatch: tier('flexBatch') },
		derived: true,
	};
}

const round = (n: number) => Math.round(n * 1e6) / 1e6;

const withRate = (p: ModelPricing, tier: Tier, band: Band, field: Field, v: number): ModelPricing => ({
	...p,
	[tier]: { ...p[tier], [band]: { ...p[tier][band], [field]: v } },
});

/**
 * Set one rate. USD edits the base pricing and keeps the headline flat rates
 * (shown on the Summary) in step with Standard ≤200K; other currencies edit
 * that currency's native pricing.
 */
export function setRate(m: ModelInfo, ccy: Currency, tier: Tier, band: Band, field: Field, v: number): ModelInfo {
	if (ccy !== 'USD') {
		const base = m.pricingByCurrency?.[ccy] ?? usdPricing(m);
		return { ...m, pricingByCurrency: { ...m.pricingByCurrency, [ccy]: withRate(base, tier, band, field, v) } };
	}
	const pricing = withRate(usdPricing(m), tier, band, field, v);
	return {
		...m,
		pricing,
		inputPricePerMillion: pricing.standard.le200k.input,
		outputPricePerMillion: pricing.standard.le200k.output,
	};
}

/** Add native pricing for a currency, seeded from the USD × FX figures shown before adding. */
export function addNativePricing(m: ModelInfo, ccy: Currency, fx: number): ModelInfo {
	if (ccy === 'USD') return m;
	return { ...m, pricingByCurrency: { ...m.pricingByCurrency, [ccy]: displayPricing(m, ccy, fx).pricing } };
}

/** Remove native pricing for a currency (estimates revert to USD × FX). */
export function removeNativePricing(m: ModelInfo, ccy: Currency): ModelInfo {
	const next = { ...m.pricingByCurrency };
	delete next[ccy];
	return { ...m, pricingByCurrency: next };
}

/** Pricing-relevant fields differ from the default entry. */
export function isPriceEdited(m: ModelInfo, def: ModelInfo | undefined): boolean {
	if (!def) return false;
	const pick = (x: ModelInfo) =>
		JSON.stringify([x.inputPricePerMillion, x.outputPricePerMillion, usdPricing(x), x.pricingByCurrency ?? {}]);
	return pick(m) !== pick(def);
}

/** Restore a model's pricing fields from its default, keeping any non-pricing edits (e.g. image weights). */
export function resetPricing(m: ModelInfo, def: ModelInfo): ModelInfo {
	return {
		...m,
		inputPricePerMillion: def.inputPricePerMillion,
		outputPricePerMillion: def.outputPricePerMillion,
		pricing: def.pricing,
		pricingByCurrency: def.pricingByCurrency,
	};
}
